import { useState, useCallback, useEffect } from "react";
import { atom, useRecoilState, useRecoilValue } from "recoil";
import { INotification } from "src/domains";
import {
  queryGetNotifications,
  queryGetNotification,
  queryGetNotificationsUnreadCount,
  mutationNotificationRead,
} from "src/graphql";
import { AppState, AppStateStatus } from "react-native";
import { EventRegister } from "react-native-event-listeners";
import {
  NotificationEvent,
  TabChangeEvent,
  LogoutEvent,
} from "src/utils/event";
import { useAuth, AuthState } from "src/hooks/cognito";

export const notificationState = atom<INotification[]>({
  key: "notificationState",
  default: [],
});

export const notificationsUnreadCountState = atom<number>({
  key: "notificationsUnreadCountState",
  default: 0,
});

export const useNotifications = () => {
  const [notifications, setNotifications] =
    useRecoilState<INotification[]>(notificationState);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (!_isMount) {
      return;
    }
    if (_isMount) {
      setLoading(true);
    }
    try {
      const response = await queryGetNotifications();
      if (_isMount) {
        setNotifications(response.data?.getNotifications?.items || []);
      }
    } catch (e) {
      console.error(e);
      if (_isMount) {
        setError(e as Error);
      }
    }
    if (_isMount) {
      setLoading(false);
    }
  }, [setNotifications]);

  const onAppStateChange = useCallback(
    (state: AppStateStatus) => {
      if (state === "active") {
        refresh();
      }
    },
    [refresh]
  );

  useEffect(() => {
    AppState.addEventListener("change", onAppStateChange);
    // TODO: Check if it works corrent (muliti hundler)
    // return () => {
    //   AppState.removeEventListener('change', onAppStateChange);
    // };
  }, [onAppStateChange]);

  useEffect(() => {
    const notificationListener = EventRegister.addEventListener(
      NotificationEvent,
      () => refresh()
    );
    return () => {
      EventRegister.removeEventListener(notificationListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const logoutListener = EventRegister.addEventListener(LogoutEvent, () => {
      setNotifications([]);
      setError(undefined);
    });
    return () => {
      EventRegister.removeEventListener(logoutListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return [notifications, loading, error, refresh] as const;
};

export const useNotification = (id: string) => {
  const [notification, setNotification] = useState<INotification | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (!id || !_isMount) {
      return;
    }
    if (_isMount) {
      setLoading(true);
    }
    try {
      const response = await queryGetNotification({ id });
      if (_isMount) {
        setNotification(response.data.getNotification);
      }
    } catch (e) {
      console.error(e);
      if (_isMount) {
        setError(e as Error);
      }
    }
    if (_isMount) {
      setLoading(false);
    }
  }, [setNotification, id]);

  useEffect(() => {
    refresh();
  }, [refresh, id]);

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return [notification, loading, error, refresh] as const;
};

export const useRootNotificationsUnreadCount = () => {
  const auth = useAuth();
  const [notificationsUnreadCount, setNotificationsUnreadCount] =
    useRecoilState<number>(notificationsUnreadCountState);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (auth !== AuthState.Auth) {
      // Skip on unauth
      return;
    }
    if (!_isMount) {
      return;
    }
    if (_isMount) {
      setLoading(true);
    }
    try {
      const response = await queryGetNotificationsUnreadCount();
      if (_isMount) {
        setNotificationsUnreadCount(
          response.data.getNotificationsUnreadCount || 0
        );
      }
    } catch (e) {
      console.error(e);
      if (_isMount) {
        setError(e as Error);
      }
    }
    if (_isMount) {
      setLoading(false);
    }
  }, [setNotificationsUnreadCount, auth]);

  const onAppStateChange = useCallback(
    (state: AppStateStatus) => {
      if (state === "active") {
        refresh();
      }
    },
    [refresh]
  );

  useEffect(() => {
    AppState.addEventListener("change", onAppStateChange);
    // TODO: Check if it works corrent (muliti hundler)
    // return () => {
    //   AppState.removeEventListener('change', onAppStateChange);
    // };
  }, [onAppStateChange]);

  useEffect(() => {
    const notificationListener = EventRegister.addEventListener(
      NotificationEvent,
      () => refresh()
    );
    const tabChangeListener = EventRegister.addEventListener(
      TabChangeEvent,
      (e) => {
        if (e.tabName === "MyPage") {
          refresh();
        }
      }
    );
    return () => {
      EventRegister.removeEventListener(notificationListener as string);
      EventRegister.removeEventListener(tabChangeListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [refresh, auth]);

  useEffect(() => {
    const logoutListener = EventRegister.addEventListener(LogoutEvent, () => {
      setNotificationsUnreadCount(0);
      setError(undefined);
    });
    return () => {
      EventRegister.removeEventListener(logoutListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return [notificationsUnreadCount, loading, error, refresh] as const;
};

export const useNotificationsUnreadCount = () => {
  return useRecoilValue<number>(notificationsUnreadCountState);
};

export const useNotificationActions = () => {
  const [, setNotifications] =
    useRecoilState<INotification[]>(notificationState);
  const [, setNotificationsUnreadCount] = useRecoilState<number>(
    notificationsUnreadCountState
  );
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (!_isMount) {
      return;
    }
    try {
      const notificationsResponse = await queryGetNotifications();
      if (_isMount) {
        setNotifications(
          notificationsResponse.data?.getNotifications?.items || []
        );
      }
      const notificationsUnreadCountResponse =
        await queryGetNotificationsUnreadCount();
      if (_isMount) {
        setNotificationsUnreadCount(
          notificationsUnreadCountResponse.data.getNotificationsUnreadCount || 0
        );
      }
    } catch (e) {
      console.error(e);
    }
  }, [setNotifications, setNotificationsUnreadCount]);

  const onAppStateChange = useCallback(
    (state: AppStateStatus) => {
      if (state === "active") {
        refresh();
      }
    },
    [refresh]
  );

  useEffect(() => {
    AppState.addEventListener("change", onAppStateChange);
    // TODO: Check if it works corrent (muliti hundler)
    // return () => {
    //   AppState.removeEventListener('change', onAppStateChange);
    // };
  }, [onAppStateChange]);

  const read = useCallback(
    async (id: string) => {
      try {
        await mutationNotificationRead(id);
        await refresh();
      } catch (e) {
        console.error(e);
      }
    },
    [refresh]
  );

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return { read } as const;
};
